import { useState } from 'react'
import { EventGroup } from './types'
import { EventCard } from './EventCard'
import { ActivityV2 } from 'models/activityV2'

interface EventDateGroupProps {
  group: EventGroup
  onPreviewRequest?: (sampleId: number) => void
  onEmailClick?: (event: ActivityV2) => void
}

export function EventDateGroup({
  group,
  onPreviewRequest,
  onEmailClick,
}: EventDateGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasMoreThan3Events = group.events.length > 3
  const eventsToShow = isExpanded ? group.events : group.events.slice(0, 3)

  return (
    <div className="space-y-2">
      {/* Date header — relative date + display date with trailing hairline. */}
      <div className="flex items-center gap-3">
        <span className="text-base text-gray-500 font-inter whitespace-nowrap tabular-nums">
          {group.relativeDate}
          {group.displayDate && group.relativeDate !== group.displayDate ? (
            <>, {group.displayDate}</>
          ) : null}
        </span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Gray container per day, with white event cards nested inside.
          Each card renders its own connector segment going down from
          below its icon into the gap below — the :last-child rule
          removes that segment from the final card so the timeline
          doesn't trail past it. */}
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="space-y-4">
          {eventsToShow.map((event, idx) => (
            <div
              key={`${event.created_at}-${idx}`}
              className="relative [&:last-child>[data-timeline-connector]]:hidden"
            >
              <div
                data-timeline-connector
                aria-hidden
                // Wrapper spans the same 36px width as the icon column
                // and flex-centers the line within it. This avoids any
                // subpixel math on the line's `left` value.
                className="absolute top-9 -bottom-4 left-0 w-9 flex justify-center"
              >
                <div className="w-px h-full bg-gray-400" />
              </div>
              <EventCard
                event={event}
                onPreviewRequest={onPreviewRequest}
                onEmailClick={onEmailClick}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Less/More toggle */}
      {hasMoreThan3Events && (
        <div className="flex justify-end pr-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            type="button"
            className="self-start text-sm font-medium text-brand-bright hover:text-brand-active transition-colors bg-transparent border-none p-0 cursor-pointer tabular-nums"
          >
            {isExpanded ? 'Show less' : `+${group.events.length - 3} more`}
          </button>
        </div>
      )}
    </div>
  )
}
